// Guest one-tap confirmation page for a chat offer (/offer/:token).
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BedDouble, CalendarDays, CheckCircle2, Clock, Loader2, Users, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const fmtDate = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '');

const Row = ({ Icon, label, value, testId }) => (
  <div className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={testId}>
    <Icon className="w-4 h-4 text-orange-500 shrink-0" /><span className="text-sm text-slate-500 w-28">{label}</span><span className="text-sm font-semibold text-slate-900">{value}</span>
  </div>
);

export default function OfferPage() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    fetch(`${API_URL}/api/offers/${token}`).then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.detail || 'Offer not found'); setOffer(d); }).catch((e) => setErr(e.message));
  }, [token]);
  const confirm = async () => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API_URL}/api/offers/${token}/confirm`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Could not confirm');
      setOffer(d);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const status = offer?.status;
  const expires = offer?.expires_at ? new Date(offer.expires_at).toLocaleString() : '';
  let banner = null;
  if (status === 'confirmed') banner = <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl p-4 flex items-center gap-2" data-testid="offer-confirmed"><CheckCircle2 className="w-5 h-5" /> <span><b>Your reservation is confirmed.</b> Payment {offer.payment === 'transfer' ? 'by bank transfer — we will send the details' : 'at the hotel on arrival'}. See you soon!</span></div>;
  else if (status && status !== 'held') banner = <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 flex items-center gap-2" data-testid="offer-expired"><XCircle className="w-5 h-5" /> This offer is no longer available ({status}). Please contact reception{offer.hotel_phone ? ` on ${offer.hotel_phone}` : ''} for a new one.</div>;
  return (
    <main className="min-h-screen bg-slate-50 py-10 px-4" data-testid="offer-page">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-slate-900 text-white p-6">
          <p className="text-[11px] uppercase tracking-[0.2em] text-orange-400 font-bold">Hotel La Rosa Negra · Ulcinj</p>
          <h1 className="text-2xl font-bold mt-1">{offer?.guest_name ? `${offer.guest_name}, your offer` : 'Your offer'}</h1>
          <p className="text-sm text-slate-300 mt-1">Prepared by our reception from your chat.</p>
        </div>
        <div className="p-6 space-y-4">
          {err && !offer && <div className="text-red-700 text-sm" data-testid="offer-error">{err}</div>}
          {!offer && !err && <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
          {offer && (
            <>
              <Row Icon={BedDouble} label="Room" value={offer.room_name} testId="offer-room" />
              <Row Icon={CalendarDays} label="Stay" value={`${fmtDate(offer.check_in)} → ${fmtDate(offer.check_out)} · ${offer.nights} night(s)`} testId="offer-dates" />
              <Row Icon={Users} label="Guests" value={`${offer.adults} adult(s)${offer.children ? ` + ${offer.children} child(ren)` : ''}`} testId="offer-guests" />
              {offer.note && <p className="text-sm text-slate-700 bg-orange-50 border border-orange-100 rounded-lg p-3" data-testid="offer-note">{offer.note}</p>}
              <div className="flex items-end justify-between pt-2">
                <div><p className="text-xs text-slate-500">€{offer.price_per_night?.toFixed(2)} / night</p><p className="text-3xl font-black text-slate-900" data-testid="offer-total">€{offer.total_eur?.toFixed(2)}</p></div>
                <p className="text-xs text-slate-500">incl. breakfast · direct rate</p>
              </div>
              {banner}
              {status === 'held' && (
                <>
                  <button type="button" onClick={confirm} disabled={busy} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg rounded-full py-3 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="offer-confirm">
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />} Confirm my reservation
                  </button>
                  <p className="text-xs text-slate-500 flex items-center gap-1 justify-center" data-testid="offer-hold"><Clock className="w-3.5 h-3.5" /> Room held for you until {expires} · pay {offer.payment === 'transfer' ? 'by bank transfer' : 'at the hotel'} · free cancellation 48 h before arrival</p>
                  {err && <div className="text-red-700 text-sm text-center" data-testid="offer-error">{err}</div>}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </main>
  );
}
